/**
 * Машина состояний софтфона и подписка на её изменения.
 *
 * React здесь не участвует специально: снимок хранится вне дерева компонентов,
 * ремаунт (StrictMode в dev монтирует дважды) не должен ронять регистрацию и звонок.
 * Снимок неизменяемый и меняет ссылку только при реальном изменении — иначе
 * useSyncExternalStore в apps/web уйдёт в бесконечный ререндер.
 */
/**
 * Ранг состояния звонка. Переход разрешён только вверх или в 'ended' —
 * так опоздавшее событие ringing после ответа не откатывает звонок назад
 * (то же правило, что у FSM звонка на сервере, спека §7.4).
 */
const CALL_STATE_RANK = {
    dialing: 1,
    ringing: 2,
    in_call: 3,
    ended: 4,
};
export function canAdvanceCall(from, to) {
    if (to === 'ended')
        return from !== 'ended';
    return CALL_STATE_RANK[to] > CALL_STATE_RANK[from];
}
const INITIAL = {
    line: 'offline',
    call: null,
    devices: {},
    isLeaderTab: false,
    lastError: null,
};
/**
 * Хранилище снимка софтфона: единственная точка, где состояние меняется.
 * Все мутаторы возвращают true, если снимок действительно изменился.
 */
export class SoftphoneStore {
    #snapshot = INITIAL;
    #listeners = new Set();
    getSnapshot() {
        return this.#snapshot;
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    setLine(line) {
        if (this.#snapshot.line === line)
            return false;
        return this.#commit({ ...this.#snapshot, line });
    }
    setLeaderTab(isLeaderTab) {
        if (this.#snapshot.isLeaderTab === isLeaderTab)
            return false;
        return this.#commit({ ...this.#snapshot, isLeaderTab });
    }
    setError(lastError) {
        if (this.#snapshot.lastError === lastError)
            return false;
        return this.#commit({ ...this.#snapshot, lastError });
    }
    /** Выбранные устройства. undefined означает «системное по умолчанию» */
    setDevices(input, output) {
        const devices = {
            ...(input !== null ? { inputId: input } : {}),
            ...(output !== null ? { outputId: output } : {}),
        };
        const current = this.#snapshot.devices;
        if (current.inputId === devices.inputId && current.outputId === devices.outputId)
            return false;
        return this.#commit({ ...this.#snapshot, devices });
    }
    /** Начало исходящего звонка. Второй звонок поверх активного не начинаем */
    startCall(call) {
        const active = this.#snapshot.call;
        if (active && active.state !== 'ended')
            return false;
        return this.#commit({
            ...this.#snapshot,
            call: {
                id: call.id,
                assignmentId: call.assignmentId,
                phone: call.phone,
                state: 'dialing',
                startedAt: call.startedAt,
                isCarousel: call.isCarousel ?? false,
                answeredAt: null,
                muted: false,
                endReason: null,
            },
        });
    }
    /** Продвижение звонка вперёд по рангу. Опоздавшее событие игнорируется */
    advanceCall(state, at) {
        const call = this.#snapshot.call;
        if (!call || !canAdvanceCall(call.state, state))
            return false;
        return this.#commit({
            ...this.#snapshot,
            call: {
                ...call,
                state,
                answeredAt: state === 'in_call' ? (call.answeredAt ?? at) : call.answeredAt,
            },
        });
    }
    endCall(reason) {
        const call = this.#snapshot.call;
        if (!call || call.state === 'ended')
            return false;
        return this.#commit({
            ...this.#snapshot,
            call: { ...call, state: 'ended', endReason: reason },
        });
    }
    /** Убрать завершённый звонок с пульта после того, как метка проставлена */
    clearCall() {
        if (this.#snapshot.call === null)
            return false;
        return this.#commit({ ...this.#snapshot, call: null });
    }
    setMuted(muted) {
        const call = this.#snapshot.call;
        if (!call || call.muted === muted)
            return false;
        return this.#commit({ ...this.#snapshot, call: { ...call, muted } });
    }
    #commit(next) {
        this.#snapshot = next;
        for (const listener of this.#listeners)
            listener(next);
        return true;
    }
}
//# sourceMappingURL=state.js.map